import React, { useEffect, useMemo, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { resumenDelDia, obtenerCortes, cerrarDia, reabrirDia } from '../services/corteService';
import { ajustarSaldo, iconoTipo } from '../services/cuentasService';
import CampoNumero from './CampoNumero';
import { mensajeAmigable } from '../utils/errores';
import { aNumero } from '../utils/validacion';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { copiarTexto } from '../utils/compartir';
import { fechaLocal } from '../utils/fecha';

const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const hoyYmd = () => fechaLocal();
const diaLabel = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const base = `${DIAS[dt.getDay()]} ${d} de ${MESES[m - 1]}`;
  if (ymd === hoyYmd()) return `Hoy · ${base}`;
  return `${base} de ${y}`;
};
const desplazar = (ymd, delta) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return fechaLocal(new Date(y, m - 1, d + delta));
};
const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CorteDiaView({ onVolver, onCambio }) {
  const { mostrarToast } = useToast();
  const { negocio } = useAuth();
  const [fecha, setFecha] = useState(hoyYmd());
  const [datos, setDatos] = useState(null);
  const [cortes, setCortes] = useState([]);
  const [conteos, setConteos] = useState({});   // cuenta_id -> string (efectivo contado)
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [cuadrandoId, setCuadrandoId] = useState(null);

  const esFuturo = fecha >= hoyYmd();
  const cerrado = cortes.find((c) => c.fecha === fecha);

  const cargar = async (dia = fecha) => {
    setError(null); setCargando(true);
    try {
      const [res, cs] = await Promise.all([resumenDelDia(dia), obtenerCortes()]);
      setDatos(res);
      setCortes(cs);
      // Precargar el conteo: si el día ya está cerrado, mostrar lo que se contó
      // ese día; si no, arrancar vacío.
      const cierre = cs.find((c) => c.fecha === dia);
      if (cierre) {
        const pre = {};
        (cierre.conteos || []).forEach((x) => { pre[x.cuenta_id] = String(x.contado ?? ''); });
        setConteos(pre);
      } else {
        setConteos({});
      }
    } catch (e) {
      setError(mensajeAmigable(e));
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar(fecha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha]);

  // Cuentas de efectivo (las únicas que se cuentan físicamente).
  const cuentasEfectivo = useMemo(
    () => (datos?.cuentas || []).filter((c) => c.tipo === 'efectivo'),
    [datos]
  );

  // Filas del conteo: en un día cerrado se muestran congeladas; en un día
  // abierto se calculan en vivo (esperado = saldo actual de la cuenta).
  const filasConteo = useMemo(() => {
    if (cerrado) {
      return (cerrado.conteos || []).map((x) => ({
        cuenta_id: x.cuenta_id, nombre: x.nombre, esperado: Number(x.esperado || 0),
        contado: Number(x.contado || 0), diferencia: Number(x.diferencia || 0), congelado: true,
      }));
    }
    return cuentasEfectivo.map((c) => {
      const esperado = Number(c.saldo || 0);
      const txt = conteos[c.id];
      const tiene = txt !== undefined && txt !== '';
      const contado = tiene ? aNumero(txt) : null;
      return { cuenta_id: c.id, nombre: c.nombre, esperado, contado, diferencia: tiene ? contado - esperado : null, congelado: false };
    });
  }, [cerrado, cuentasEfectivo, conteos]);

  const cuadrarCaja = async (fila) => {
    setCuadrandoId(fila.cuenta_id);
    try {
      await ajustarSaldo(fila.cuenta_id, fila.contado, `Cuadre de caja ${fecha}`);
      mostrarToast('Caja cuadrada ✓ (se registró el ajuste)', 'exito');
      onCambio && onCambio();
      await cargar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setCuadrandoId(null);
    }
  };

  const hacerCierre = async () => {
    const sinContar = cuentasEfectivo.filter((c) => !(conteos[c.id] !== undefined && conteos[c.id] !== ''));
    if (sinContar.length > 0) {
      const ok = window.confirm(
        `No contaste el efectivo de ${sinContar.map((c) => c.nombre).join(', ')}. ¿Cerrar el día de todos modos?`
      );
      if (!ok) return;
    }
    const conDescuadre = filasConteo.filter((f) => f.diferencia != null && Math.abs(f.diferencia) >= 0.01);
    if (conDescuadre.length > 0) {
      const detalle = conDescuadre
        .map((f) => `${f.nombre}: ${f.diferencia > 0 ? 'sobran' : 'faltan'} ${fmt(Math.abs(f.diferencia))}`)
        .join('\n');
      const ok = window.confirm(
        `Hay descuadre en la caja:\n${detalle}\n\nPuedes cerrar así (queda registrado el descuadre) o cancelar y tocar "Cuadrar" primero. ¿Cerrar así?`
      );
      if (!ok) return;
    }
    setGuardando(true);
    try {
      const conteosSnap = filasConteo
        .filter((f) => f.contado != null)
        .map((f) => ({ cuenta_id: f.cuenta_id, nombre: f.nombre, esperado: f.esperado, contado: f.contado, diferencia: f.diferencia }));
      await cerrarDia(fecha, {
        ingresos: datos.ingresos, egresos: datos.egresos, neto: datos.neto,
        ventas_dia: datos.ventasDia, tickets_dia: datos.ticketsDia,
        por_canal: datos.porCanal, saldo_cuentas: datos.saldoCuentas,
      }, conteosSnap);
      mostrarToast(`Día cerrado ✓`, 'exito');
      await cargar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  const abrir = async () => {
    try {
      await reabrirDia(fecha);
      mostrarToast('Día reabierto', 'exito');
      await cargar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    }
  };

  // Texto del corte (formato WhatsApp: *negrita*) para compartir o guardar.
  const textoDelCorte = () => {
    if (!datos) return '';
    const L = [];
    L.push(`🧾 *Corte del día — ${negocio?.nombre || 'Mi negocio'}*`);
    L.push(`📅 ${diaLabel(fecha)}`);
    L.push('');
    L.push('💰 *Movimiento del día*');
    L.push(`• Entró: ${fmt(datos.ingresos)}`);
    L.push(`• Salió: ${fmt(datos.egresos)}`);
    L.push(`• Neto: ${fmt(datos.neto)}`);
    L.push(`🛍️ Vendido: ${fmt(datos.ventasDia)}${datos.ticketsDia ? ` (${datos.ticketsDia} venta${datos.ticketsDia === 1 ? '' : 's'})` : ''}`);
    if (datos.porCanal.length) {
      L.push('');
      L.push('📣 *Ventas por canal*');
      datos.porCanal.forEach((c) => {
        const pct = datos.ventasDia > 0 ? Math.round((c.monto / datos.ventasDia) * 100) : 0;
        L.push(`• ${c.canal}: ${fmt(c.monto)} (${pct}%)`);
      });
    }
    const movCuentas = (datos.porCuenta || [])
      .filter((c) => (Number(c.ingresos || 0) + Number(c.egresos || 0)) > 0.005)
      .sort((a, b) => (b.ingresos - b.egresos) - (a.ingresos - a.egresos));
    if (movCuentas.length) {
      L.push('');
      L.push('🏦 *Por cuenta*');
      movCuentas.forEach((c) => {
        const neto = Number(c.ingresos || 0) - Number(c.egresos || 0);
        L.push(`• ${c.nombre}: ${neto >= 0 ? '+' : '−'}${fmt(Math.abs(neto))}`);
      });
    }
    if (filasConteo.length) {
      L.push('');
      L.push('🧾 *Efectivo*');
      filasConteo.forEach((f) => {
        if (f.diferencia == null) {
          L.push(`• ${f.nombre}: sin contar (sistema ${fmt(f.esperado)})`);
        } else {
          const cuadra = Math.abs(f.diferencia) < 0.01;
          const estado = cuadra ? '✅ cuadra' : (f.diferencia > 0 ? `▲ sobran ${fmt(f.diferencia)}` : `▼ faltan ${fmt(Math.abs(f.diferencia))}`);
          L.push(`• ${f.nombre}: contado ${fmt(f.contado)} / sistema ${fmt(f.esperado)} — ${estado}`);
        }
      });
    }
    L.push('');
    L.push(cerrado ? '🔒 Día cerrado' : '📋 Borrador (día aún sin cerrar)');
    L.push('— Kontrol · Automatika.IA');
    return L.join('\n');
  };

  const compartirWA = () => {
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(textoDelCorte())}`, '_blank', 'noopener,noreferrer');
  };
  const copiarCorte = async () => {
    const ok = await copiarTexto(textoDelCorte());
    mostrarToast(ok ? 'Corte copiado 📋 (pegalo donde quieras)' : 'No se pudo copiar', ok ? 'exito' : 'error');
  };

  const tarjeta = { backgroundColor: '#fff', borderRadius: '16px', padding: '16px', boxShadow: '0 1px 2px rgba(28,24,48,.04), 0 4px 16px rgba(76,29,149,.05)', border: '1px solid #ECE8F6', marginBottom: '16px' };
  const input = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };

  return (
    <div className="k-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <h2 className="k-page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--color-primario,#DB2777)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19" />
          </svg>
          Corte del Día
        </h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '2px' }}>
            <button onClick={() => setFecha(desplazar(fecha, -1))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', fontSize: '16px', color: '#6B7280' }}>‹</button>
            <span style={{ minWidth: '170px', textAlign: 'center', fontWeight: 700, fontSize: '13px', color: '#374151' }}>{diaLabel(fecha)}</span>
            <button onClick={() => !esFuturo && setFecha(desplazar(fecha, 1))} disabled={esFuturo}
              style={{ background: 'none', border: 'none', cursor: esFuturo ? 'not-allowed' : 'pointer', padding: '4px 8px', fontSize: '16px', color: esFuturo ? '#D1D5DB' : '#6B7280' }}>›</button>
          </div>
          <button onClick={onVolver} className="k-btn-soft">← Volver</button>
        </div>
      </div>

      {error && <p style={{ textAlign: 'center', padding: '16px', color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</p>}
      {cargando && !datos && <p style={{ textAlign: 'center', padding: '24px', color: '#9CA3AF' }}>⏳ Cargando…</p>}

      {datos && (
        <>
          {/* Estado del día */}
          {cerrado ? (
            <div style={{ ...tarjeta, display: 'flex', alignItems: 'center', gap: '12px', background: '#ECFDF5', border: '1px solid #A7F3D0' }}>
              <span style={{ fontSize: '26px' }}>🔒</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontWeight: 'bold', color: '#065F46' }}>Día cerrado</p>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#047857' }}>
                  Neto del día {fmt(cerrado.resumen?.neto)} · cerrado el {fechaLocal(new Date(cerrado.cerrado_en))}
                </p>
              </div>
              <button onClick={abrir} style={{ background: 'white', border: '1px solid #6EE7B7', color: '#047857', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontWeight: 700 }}>
                🔓 Reabrir
              </button>
            </div>
          ) : null}

          {/* Resumen del día */}
          <div style={tarjeta}>
            <p style={{ margin: '0 0 12px', fontWeight: 700, color: '#374151' }}>💰 Movimiento del día</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
              <ResumenBox etiqueta="Entró hoy" valor={fmt(datos.ingresos)} color="var(--k-verde)" />
              <ResumenBox etiqueta="Salió hoy" valor={fmt(datos.egresos)} color="#DC2626" />
              <ResumenBox etiqueta="Neto del día" valor={fmt(datos.neto)} color={datos.neto >= 0 ? 'var(--k-verde)' : '#DC2626'} />
              <ResumenBox etiqueta={`Vendido hoy${datos.ticketsDia ? ` · ${datos.ticketsDia} venta${datos.ticketsDia === 1 ? '' : 's'}` : ''}`} valor={fmt(datos.ventasDia)} color="#374151" />
            </div>
            <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
              «Entró/Salió» = dinero que se movió hoy en las cuentas. «Vendido» incluye ventas al crédito (fiado), aunque el dinero entre después.
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px' }}>
              <button onClick={compartirWA}
                style={{ flex: '1 1 160px', padding: '10px', background: 'var(--k-verde)', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 700, cursor: 'pointer', fontSize: '14px' }}>
                📲 Compartir por WhatsApp
              </button>
              <button onClick={copiarCorte}
                style={{ flex: '1 1 120px', padding: '10px', background: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '10px', fontWeight: 700, cursor: 'pointer', fontSize: '14px' }}>
                📋 Copiar / respaldo
              </button>
            </div>
          </div>

          {/* Ventas por canal */}
          {datos.porCanal.length > 0 && (
            <div style={tarjeta}>
              <p style={{ margin: '0 0 10px', fontWeight: 700, color: '#374151' }}>📣 Ventas de hoy por canal</p>
              {datos.porCanal.map((c) => {
                const pct = datos.ventasDia > 0 ? Math.round((c.monto / datos.ventasDia) * 100) : 0;
                return (
                  <div key={c.canal} style={{ marginBottom: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#374151', marginBottom: '3px' }}>
                      <span>{c.canal} <span style={{ color: '#9CA3AF' }}>· {c.tickets} venta{c.tickets === 1 ? '' : 's'}</span></span>
                      <span style={{ fontWeight: 700 }}>{fmt(c.monto)} <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({pct}%)</span></span>
                    </div>
                    <div style={{ height: '6px', background: '#F3F4F6', borderRadius: '999px', overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: 'var(--color-primario,#DB2777)' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Movimiento por cuenta */}
          {(() => {
            const movCuentas = (datos.porCuenta || [])
              .filter((c) => (Number(c.ingresos || 0) + Number(c.egresos || 0)) > 0.005)
              .sort((a, b) => (b.ingresos - b.egresos) - (a.ingresos - a.egresos));
            if (movCuentas.length === 0) return null;
            return (
              <div style={tarjeta}>
                <p style={{ margin: '0 0 4px', fontWeight: 700, color: '#374151' }}>🏦 ¿A qué cuenta entró/salió hoy?</p>
                <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#9CA3AF' }}>
                  Cuánto se movió hoy en cada caja (efectivo, banco, POS…).
                </p>
                {movCuentas.map((c) => {
                  const neto = Number(c.ingresos || 0) - Number(c.egresos || 0);
                  const ambos = Number(c.ingresos || 0) > 0.005 && Number(c.egresos || 0) > 0.005;
                  return (
                    <div key={c.cuenta_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: 0, fontWeight: 600, color: '#111827' }}>{iconoTipo(c.tipo)} {c.nombre}</p>
                        {ambos && (
                          <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
                            entró {fmt(c.ingresos)} · salió {fmt(c.egresos)}
                          </p>
                        )}
                      </div>
                      <span style={{ fontWeight: 'bold', color: neto >= 0 ? 'var(--k-verde)' : '#DC2626' }}>
                        {neto >= 0 ? '+' : '−'}{fmt(Math.abs(neto))}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Conteo de efectivo */}
          <div style={tarjeta}>
            <p style={{ margin: '0 0 4px', fontWeight: 700, color: '#374151' }}>🧾 Conteo de efectivo</p>
            <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#9CA3AF' }}>
              Cuenta los billetes y monedas de cada caja y escribe el total. El sistema te dice si sobra o falta.
            </p>
            {filasConteo.length === 0 && (
              <p style={{ textAlign: 'center', padding: '16px', color: '#9CA3AF' }}>
                No tienes cuentas de tipo «Efectivo». Crea una en 🏦 Cuentas para poder cuadrar la caja.
              </p>
            )}
            {filasConteo.map((f) => {
              const cuadra = f.diferencia != null && Math.abs(f.diferencia) < 0.01;
              const sobra = f.diferencia != null && f.diferencia > 0;
              const difColor = f.diferencia == null ? '#9CA3AF' : (cuadra ? 'var(--k-verde)' : (sobra ? '#B45309' : '#DC2626'));
              return (
                <div key={f.cuenta_id} style={{ padding: '12px 0', borderBottom: '1px solid #F3F4F6' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: 0, fontWeight: 600, color: '#111827' }}>💵 {f.nombre}</p>
                      <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6B7280' }}>Según el sistema: <b>{fmt(f.esperado)}</b></p>
                    </div>
                    <div style={{ width: '160px' }}>
                      {f.congelado ? (
                        <p style={{ margin: 0, textAlign: 'right', fontWeight: 700, color: '#374151' }}>Contado: {fmt(f.contado)}</p>
                      ) : (
                        <CampoNumero
                          valor={conteos[f.cuenta_id] ?? ''}
                          onChange={(v) => setConteos((c) => ({ ...c, [f.cuenta_id]: v }))}
                          min={0} prefijo="L" placeholder="Contado" estilo={input}
                        />
                      )}
                    </div>
                  </div>
                  {f.diferencia != null && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap', gap: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: difColor }}>
                        {cuadra ? '✅ Cuadra perfecto' : (sobra ? `▲ Sobran ${fmt(f.diferencia)}` : `▼ Faltan ${fmt(Math.abs(f.diferencia))}`)}
                      </span>
                      {!f.congelado && !cuadra && !cerrado && (
                        <button onClick={() => cuadrarCaja(f)} disabled={cuadrandoId === f.cuenta_id}
                          style={{ padding: '6px 12px', background: 'white', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 700, color: '#374151' }}>
                          {cuadrandoId === f.cuenta_id ? '⏳…' : '⚖️ Cuadrar caja a lo contado'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {!cerrado && filasConteo.some((f) => !f.congelado && f.diferencia != null && Math.abs(f.diferencia) >= 0.01) && (
              <p style={{ margin: '12px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
                «Cuadrar caja» registra un ajuste para que el sistema quede igual a lo que contaste (queda en el historial de la cuenta).
              </p>
            )}
          </div>

          {/* Cerrar el día */}
          {!cerrado && (
            <button onClick={hacerCierre} disabled={guardando}
              style={{ width: '100%', padding: '14px', backgroundColor: guardando ? '#9CA3AF' : 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', fontSize: '15px', cursor: guardando ? 'wait' : 'pointer' }}>
              {guardando ? '⏳ Cerrando…' : `🔒 Cerrar ${fecha === hoyYmd() ? 'el día de hoy' : 'este día'}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function ResumenBox({ etiqueta, valor, color }) {
  return (
    <div style={{ background: '#F9FAFB', borderRadius: '10px', padding: '12px', border: '1px solid #F3F4F6' }}>
      <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#6B7280', fontWeight: 600 }}>{etiqueta}</p>
      <p style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color }}>{valor}</p>
    </div>
  );
}
